// frontend/src/pages/Home.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  ChevronLeft, 
  ChevronRight, 
  Heart, 
  MessageCircle, 
  Eye, 
  Calendar, 
  User,
  ArrowLeft,
  ShoppingBag,
  Image,
  BookOpen,
  Star,
  Users,
  Award,
  TrendingUp,
  FolderOpen,
  Settings, Zap, PhoneCall, Wrench,
  Grid3X3,
  Clock,
  Target, EyeIcon, CheckCircle,
  
} from 'lucide-react';

const getIconComponent = (iconName) => {
    const icons = {
      'users': Users,
      'target': Target,
      'eye': EyeIcon,
      'award': Award,
      'heart': Heart,
      'shield': CheckCircle,
      'clock': Clock,
      'trending-up': TrendingUp,
      'star': Star,
      'check-circle': CheckCircle,
    };
    
    const IconComponent = icons[iconName] || Award;
    return <IconComponent className="w-6 h-6" />;
  };

const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [aboutData, setAboutData] = useState(null);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [servicesData, setServicesData] = useState(null);
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState({});
  const [activeBlogTab, setActiveBlogTab] = useState('all');
  const [blogLoading, setBlogLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    fetchHomeData();
    fetchHomeData();
    fetchGalleryCategories();
    fetchBlogCategories();
    fetchServicesData();
  }, []);

  useEffect(() => {
    if (activeBlogTab) {
    // if (activeBlogTab && blogCategories.length > 0) {
      fetchBlogPosts(activeBlogTab);
    }
  }, [activeBlogTab]);

  const fetchHomeData = async () => {
    try {
      const response = await apiService.getHomeData();
      setHomeData(response.data);

      if (response.data.about_preview) {
      setAboutData(response.data.about_preview);
    }
    } catch (error) {
      console.error('Error fetching home data:', error);
    } finally {
    setLoading(false);
  }
  };

  const fetchAboutData = async () => {
    try {
      const response = await apiService.getAboutData();
      setAboutData(response.data);
    } catch (error) {
      console.error('Error fetching about data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ارزش‌های پیشفرض اگر از API دریافت نشد
  const defaultValues = [
    {
      id: 1,
      title: 'کیفیت برتر',
      description: 'تضمین کیفیت در تمامی محصولات و خدمات ارائه شده',
      icon: 'award'
    },
    {
      id: 2,
      title: 'پشتیبانی حرفه‌ای',
      description: 'پشتیبانی ۲۴ ساعته و خدمات پس از فروش ممتاز',
      icon: 'shield'
    },
    {
      id: 3,
      title: 'نوآوری مستمر',
      description: 'همگام با تکنولوژی‌های روز دنیا در صنعت',
      icon: 'trending-up'
    },
    {
      id: 4,
      title: 'رضایت مشتری',
      description: 'اولویت اصلی ما رضایت کامل مشتریان است',
      icon: 'heart'
    }
  ];

  const AnimatedCounter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const increment = end / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.ceil(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}+</span>;
};

  // Blog Tab
  const fetchBlogPosts = async (categorySlug) => {
    try {
      const params = {};
      if (categorySlug !== 'all') {
        params.category = categorySlug;
      }
      
      const response = await apiService.getPosts(params);
      const posts = response.data.results || response.data;
      
      setBlogPosts(prev => ({
        ...prev,
        [categorySlug]: posts.slice(0, 6) // فقط 6 پست اول
      }));
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setBlogPosts(prev => ({
        ...prev,
        [categorySlug]: []
      }));
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPosts = () => {
    return blogPosts[activeBlogTab] || [];
  };
  // Blog Tab

  const fetchServicesData = async () => {
    try {
      const response = await apiService.getServicesHomeData();
      setServicesData(response.data);
    } catch (error) {
      console.error('Error fetching services data:', error);
    } finally {
      setLoading(false);
    }
  };

  // تابع برای گرفتن آیکون سرویس
  const getServiceIcon = (iconName) => {
    const icons = {
      'Settings': Settings,
      'Wrench': Wrench,
      'PhoneCall': PhoneCall,
      'BookOpen': BookOpen,
      'Users': Users,
      'Zap': Zap,
    };
    return icons[iconName] || Settings;
  };

  // تابع برای گرفتن آیکون نوع خدمت
  const getServiceTypeIcon = (type) => {
    const icons = {
      'consulting': Users,
      'implementation': Settings,
      'support': PhoneCall,
      'training': BookOpen,
      'maintenance': Wrench,
    };
    return icons[type] || Settings;
  };

  const fetchGalleryCategories = async () => {
    try {
      const response = await apiService.getGalleryCategories();
      setGalleryCategories(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching gallery categories:', error);
    }
  };

  const fetchBlogCategories = async () => {
    try {
      const response = await apiService.getBlogCategories();
      setBlogCategories(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching blog categories:', error);
    }
  };

  const toggleLike = (postId) => {
    const newLikedPosts = new Set(likedPosts);
    if (newLikedPosts.has(postId)) {
      newLikedPosts.delete(postId);
    } else {
      newLikedPosts.add(postId);
    }
    setLikedPosts(newLikedPosts);
  };

  const nextSlide = () => {
    if (homeData?.gallery_images) {
      setCurrentSlide((prev) => 
        prev === homeData.gallery_images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevSlide = () => {
    if (homeData?.gallery_images) {
      setCurrentSlide((prev) => 
        prev === 0 ? homeData.gallery_images.length - 1 : prev - 1
      );
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setAutoPlay(false); // وقتی کاربر دستی اسلاید عوض کند، auto-play متوقف شود
    setTimeout(() => setAutoPlay(true), 10000); // بعد از 10 ثانیه auto-play دوباره شروع شود
  };

  // تابع برای ساخت URL کامل تصاویر
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/api/placeholder/400/300';
    if (imagePath.startsWith('http')) return imagePath;
    return `http://localhost:8000${imagePath}`;
  };
  
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'اخیراً';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'دیروز';
    if (diffDays < 7) return `${diffDays} روز پیش`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} هفته پیش`;
    return `${Math.ceil(diffDays / 30)} ماه پیش`;
  };

  // آمار شرکت
  const companyStats = [
    { number: '۲۰+', label: 'سال تجربه', icon: Award },
    { number: '۵۰۰+', label: 'پروژه موفق', icon: TrendingUp },
    { number: '۱٬۰۰۰+', label: 'مشتری راضی', icon: Users },
    { number: '۵۰+', label: 'کارشناس متخصص', icon: Star },
  ];


  useEffect(() => {
    if (homeData && galleryCategories.length > 0 && blogCategories.length > 0) {
      setLoading(false);
    }
  }, [homeData, galleryCategories, blogCategories]);
  

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="گروه صنعتی تکنو پلیمر اسپادانا - قالب سازی و لاستیک سازی"
        description=" گروه صنعتی تکنو پلیمر اسپادانا طراحی و ساخت انواع قالب های لاستیک و تولید انواع قطعات لاستیکی از جمله اورینگ، پکینگ، قطعات صنعتی و خودرویی با انواع متریال (EPDM, HNBR, VITON, SILICON, SBR, NBR)"
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-home.jpg')}
      />
      
      <div className="min-h-screen bg-black">
        {/* Hero Section با عکس Background */}
        <section className="relative h-screen flex items-center justify-center">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('headersite05.jpg')`,
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-60"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
              گروه صنعتی <span className="text-yellow-400">تکنو پلیمر اسپادانا</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
              طراحی و ساخت انواع قالب های لاستیک و تولید انواع قطعات لاستیکی
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-yellow-300 transition-all transform hover:scale-105 shadow-lg"
              >
                🛍️ مشاهده محصولات
              </Link>
              <Link
                to="/about"
                className="border-2 border-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-gray-900 transition-all transform hover:scale-105"
              >
                ℹ️ درباره ما
              </Link>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="py-20 bg-black relative overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 space-x-reverse bg-gray-800/50 backdrop-blur-sm rounded-full px-6 py-3 border border-gray-700 mb-6">
                <TrendingUp className="w-5 h-5 text-yellow-400" />
                <span className="text-gray-300 text-sm">رشد مستمر در صنعت</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                ما در <span className="text-yellow-400">اعداد</span> صحبت می‌کنیم
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                ارقامی که گواه کیفیت و تعهد ما به مشتریان هستند
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* سال تجربه */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-yellow-400 transition-all duration-500 group hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-yellow-400/20 rounded-xl group-hover:bg-yellow-400/30 transition-colors">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div className="text-2xl font-bold text-yellow-400">۱۰+</div>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">سال تجربه</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  دو دهه فعالیت موفق در صنعت تولید
                </p>
                <div className="mt-4 flex items-center space-x-2 space-x-reverse text-yellow-400 text-xs">
                  <TrendingUp className="w-4 h-4" />
                  <span>رشد ۲۰% سالانه</span>
                </div>
              </div>

              {/* پروژه موفق */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-green-400 transition-all duration-500 group hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-400/20 rounded-xl group-hover:bg-green-400/30 transition-colors">
                    <Award className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="text-2xl font-bold text-green-400">۵۰۰+</div>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">پروژه موفق</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  پروژه‌های تحویل داده شده با کیفیت
                </p>
                <div className="mt-4 flex items-center space-x-2 space-x-reverse text-green-400 text-xs">
                  <TrendingUp className="w-4 h-4" />
                  <span>۱۵% رشد پروژه‌ها</span>
                </div>
              </div>

              {/* مشتری راضی */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-blue-400 transition-all duration-500 group hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-400/20 rounded-xl group-hover:bg-blue-400/30 transition-colors">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="text-2xl font-bold text-blue-400">۱K+</div>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">مشتری راضی</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  رضایت ۹۸% از خدمات و محصولات
                </p>
                <div className="mt-4 flex items-center space-x-2 space-x-reverse text-blue-400 text-xs">
                  <TrendingUp className="w-4 h-4" />
                  <span>۹۸% رضایت مشتری</span>
                </div>
              </div>

              {/* کارشناس متخصص */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-purple-400 transition-all duration-500 group hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-400/20 rounded-xl group-hover:bg-purple-400/30 transition-colors">
                    <Star className="w-6 h-6 text-purple-400" />
                  </div>
                  <div className="text-2xl font-bold text-purple-400">۱۰+</div>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">کارشناس متخصص</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  تیم متخصص و با تجربه صنعتی
                </p>
                <div className="mt-4 flex items-center space-x-2 space-x-reverse text-purple-400 text-xs">
                  <TrendingUp className="w-4 h-4" />
                  <span>۲۵% رشد تیم</span>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-12">
              {[
                { label: "ISO 9001 Certified", color: "green" },
                { label: "Award Winning", color: "yellow" },
                { label: "24/7 Support", color: "blue" },
                { label: "Quality Guarantee", color: "purple" }
              ].map((badge, index) => (
                <div key={index} className="flex items-center space-x-2 space-x-reverse bg-gray-800/50 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-700">
                  <div className={`w-2 h-2 rounded-full bg-${badge.color}-400`}></div>
                  <span className="text-gray-300 text-sm">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* End Statistics Section */}

        {/* Service Categories Section */}
        {servicesData?.main_categories && servicesData.main_categories.length > 0 && (
          <section className="py-16 bg-black">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">📂 خدمات تکنو پلیمر اسپادانا</h2>
                <p className="text-gray-400 text-lg">خدمات ما در حوزه‌های مختلف صنعتی</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {servicesData.main_categories.map((category) => {
                  const CategoryIcon = getServiceIcon(category.icon);
                  return (
                    <Link
                      key={category.id}
                      to={`/services?category=${category.slug}`}
                      className="bg-gray-800 rounded-2xl p-6 text-center hover:bg-yellow-400 hover:text-gray-900 transition-all group"
                    >
                      <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                        <CategoryIcon className="w-8 h-8 text-yellow-400 group-hover:text-gray-900 transition-colors" />
                      </div>
                      <h3 className="font-bold text-white group-hover:text-gray-900 transition-colors mb-2">
                        {category.name}
                      </h3>
                      <p className="text-gray-400 group-hover:text-gray-700 text-sm">
                        {/* {category.services_count || 0} خدمت */}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Featured Services Section */}
        {servicesData?.featured_services && servicesData.featured_services.length > 0 && (
          <section className="py-16 bg-gray-900">
            <div className="container mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">⭐ خدمات ویژه</h2>
                <p className="text-gray-400 text-lg">خدمات تخصصی ما برای موفقیت کسب‌وکار شما</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {servicesData.featured_services.map((service) => {
                  const ServiceTypeIcon = getServiceTypeIcon(service.service_type);
                  return (
                    <div key={service.id} className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={getFullImageUrl(service.featured_image)}
                          alt={service.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                          ویژه
                        </div>
                        <div className="absolute top-3 right-3 bg-gray-800 text-gray-300 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 space-x-reverse">
                          <ServiceTypeIcon size={12} />
                          <span>
                            {service.service_type === 'consulting' && 'مشاوره'}
                            {service.service_type === 'implementation' && 'اجرا'}
                            {service.service_type === 'support' && 'پشتیبانی'}
                            {service.service_type === 'training' && 'آموزش'}
                            {service.service_type === 'maintenance' && 'نگهداری'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-5">
                        <h3 className="font-bold text-lg text-white mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
                          {service.name}
                        </h3>
                        
                        <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                          {service.short_description}
                        </p>

                        <div className="flex items-center justify-between text-gray-400 text-sm border-t border-gray-800 pt-4">
                          <div className="flex items-center space-x-3 space-x-reverse">
                            {service.duration && (
                              <div className="flex items-center space-x-1 space-x-reverse">
                                <span>{service.duration}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-1 space-x-reverse">
                              <Eye size={14} />
                              <span>{service.view_count}</span>
                            </div>
                          </div>
                          
                          <Link
                            to={`/services/${service.slug}`}
                            className="text-yellow-400 hover:text-yellow-300 font-medium text-sm"
                          >
                            جزئیات بیشتر
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/services"
                  className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
                >
                  <span>مشاهده همه خدمات</span>
                  <ArrowLeft size={20} />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Product Categories */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 space-x-reverse mb-4">
                <ShoppingBag className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">محصولات ما</h2>
              </div>
              <p className="text-gray-400 text-lg">تنوع گسترده در محصولات صنعتی</p>
            </div>

            {homeData?.main_categories?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {homeData.main_categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/products?category=${category.slug}`}
                    className="bg-gray-900 rounded-2xl p-6 text-center hover:bg-yellow-400 hover:text-gray-900 transition-all group border border-gray-800 hover:border-yellow-400"
                  >
                    <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                      {category.image ? (
                        <img
                          src={getFullImageUrl(category.image)}
                          alt={category.name}
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <FolderOpen className="w-8 h-8 text-yellow-400" />
                      )}
                    </div>
                    <h3 className="font-bold text-white group-hover:text-gray-900 transition-colors text-sm">
                      {category.name}
                    </h3>
                    <p className="text-gray-400 group-hover:text-gray-700 text-xs mt-2 line-clamp-2">
                      {category.description}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShoppingBag className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز دسته‌بندی محصولی ایجاد نشده است.</p>
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/products"
                className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
              >
                <span>مشاهده همه محصولات</span>
                <ArrowLeft size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 space-x-reverse mb-4">
                <Star className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">⭐ محصولات ویژه</h2>
              </div>
              <p className="text-gray-400 text-lg">با کیفیت ترین محصولات صنعتی ما</p>
            </div>
            
            {homeData?.featured_products?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {homeData.featured_products.map((product) => (
                  <div key={product.id} className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getFullImageUrl(product.main_image)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {product.is_featured && (
                        <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                          ویژه
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg text-white mb-2 line-clamp-2 group-hover:text-yellow-400 transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {product.short_description}
                      </p>
                      {product.price && (
                        <div className="flex justify-between items-center">
                          <span className="text-yellow-400 font-bold text-lg">
                            {product.price.toLocaleString()} تومان
                          </span>
                          <Link
                            to={`/products/${product.slug}`}
                            className="text-yellow-400 hover:text-yellow-300 text-sm font-medium border border-yellow-400 px-3 py-1 rounded-lg hover:bg-yellow-400 hover:text-gray-900 transition-all"
                          >
                            جزئیات
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز محصول ویژه‌ای اضافه نشده است.</p>
              </div>
            )}
          </div>
        </section>
        
        

        {/* Gallery Categories */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 space-x-reverse mb-4">
                <Image className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">گالری تصاویر</h2>
              </div>
              <p className="text-gray-400 text-lg">دسته‌بندی‌های مختلف گالری تصاویر</p>
            </div>

            {galleryCategories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {galleryCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/gallery?category=${category.slug}`}
                    className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={getFullImageUrl(category.cover_image) || getFullImageUrl('/api/placeholder/300/200')}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-20 transition-all"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="font-bold text-lg mb-1">{category.name}</h3>
                        <p className="text-gray-200 text-sm line-clamp-2">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Image className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز دسته‌بندی گالری ایجاد نشده است.</p>
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/gallery"
                className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
              >
                <span>مشاهده کامل گالری</span>
                <ArrowLeft size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Gallery Slider */}
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">📸 گالری تصاویر</h2>
              <p className="text-gray-400 text-lg">نمونه کارها و پروژه‌های ما</p>
            </div>

            {homeData?.gallery_images?.length > 0 ? (
              <div className="relative max-w-6xl mx-auto">
                <div className="overflow-hidden rounded-2xl bg-black">
                  <div className="relative h-96 md:h-[500px]">
                    {/* نمایش تصویر فعلی */}
                    {homeData.gallery_images.map((image, index) => (
                      <div
                        key={image.id}
                        className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                          index === currentSlide ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <img
                          src={getFullImageUrl(image.image)}
                          alt={image.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h3 className="text-2xl font-bold mb-2">{image.title}</h3>
                          <p className="text-gray-200">{image.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Buttons */}
                {homeData.gallery_images.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all z-10"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all z-10"
                    >
                      <ChevronLeft size={24} />
                    </button>
                  </>
                )}

                {/* Progress Dots */}
                {homeData.gallery_images.length > 1 && (
                  <div className="flex justify-center mt-6 space-x-2 space-x-reverse">
                    {homeData.gallery_images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          index === currentSlide 
                            ? 'bg-yellow-400 w-8' 
                            : 'bg-gray-600 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                )}

                {/* Slide Counter */}
                {homeData.gallery_images.length > 1 && (
                  <div className="text-center mt-4">
                    <span className="text-gray-400 text-sm">
                      {currentSlide + 1} / {homeData.gallery_images.length}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Award className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز تصویری در گالری اضافه نشده است.</p>
              </div>
            )}
          </div>
        </section>
        

      {/* Recent Posts - Tab Style */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">📝 آخرین مقالات</h2>
            <p className="text-gray-400 text-lg">جدیدترین مطالب آموزشی و اخبار صنعت</p>
          </div>

          {/* Blog Tabs */}
          <div className="max-w-6xl mx-auto">
            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {/* Tab All */}
              <button
                onClick={() => setActiveBlogTab('all')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  activeBlogTab === 'all'
                    ? 'bg-yellow-400 text-gray-900 shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                همه مقالات
              </button>

              {/* Category Tabs */}
              {blogCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveBlogTab(category.slug)}
                  className={`px-6 py-3 rounded-full font-medium transition-all ${
                    activeBlogTab === category.slug
                      ? 'bg-yellow-400 text-gray-900 shadow-lg'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              {loading && !getCurrentPosts().length ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                </div>
              ) : getCurrentPosts().length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getCurrentPosts().map((post) => (
                    <BlogPostCard 
                      key={post.id} 
                      post={post} 
                      liked={likedPosts.has(post.id)}
                      onLike={() => toggleLike(post.id)}
                      timeAgo={getTimeAgo(post.published_at)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">📝</div>
                  <h3 className="text-xl font-medium text-white mb-2">مقاله‌ای یافت نشد</h3>
                  <p className="text-gray-400">هنوز مقاله‌ای در این دسته‌بندی منتشر نشده است.</p>
                </div>
              )}

              {/* View All Button */}
              <div className="text-center mt-8 pt-6 border-t border-gray-800">
                <Link
                  to="/blog"
                  className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
                >
                  <span>مشاهده همه مقالات</span>
                  <ChevronLeft size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* Blog Categories */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center space-x-3 space-x-reverse mb-4">
                <BookOpen className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">دسته‌بندی‌های بلاگ</h2>
              </div>
              <p className="text-gray-400 text-lg">موضوعات مختلف مقالات و مطالب آموزشی</p>
            </div>

            {blogCategories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {blogCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/blog?category=${category.slug}`}
                    className="bg-gray-900 rounded-2xl p-6 text-center hover:bg-yellow-400 hover:text-gray-900 transition-all group border border-gray-800 hover:border-yellow-400"
                  >
                    <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                      <Grid3X3 className="w-8 h-8 text-yellow-400" />
                    </div>
                    <h3 className="font-bold text-white group-hover:text-gray-900 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-400 group-hover:text-gray-700 text-sm mt-2 line-clamp-2">
                      {category.description}
                    </p>
                    <div className="mt-3 text-xs text-gray-500 group-hover:text-gray-600">
                      {category.posts_count || 0} مقاله
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز دسته‌بندی بلاگ ایجاد نشده است.</p>
              </div>
            )}

            <div className="text-center mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
              >
                <span>مشاهده همه مقالات</span>
                <ArrowLeft size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Recent Posts */}
        {/* <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">📝 آخرین مقالات</h2>
              <p className="text-gray-400 text-lg">مطالب آموزشی و اخبار صنعت</p>
            </div>

            {homeData?.recent_posts?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {homeData.recent_posts.map((post) => (
                  <article key={post.id} className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getFullImageUrl(post.featured_image)}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
                        {post.category_name}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-bold text-xl text-white mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-300 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <User size={16} />
                          <span>{post.author_name}</span>
                        </div>
                        <div className="flex items-center space-x-4 space-x-reverse">
                          <div className="flex items-center space-x-1 space-x-reverse">
                            <Eye size={16} />
                            <span>{post.view_count || 0}</span>
                          </div>
                          <div className="flex items-center space-x-1 space-x-reverse">
                            <Calendar size={16} />
                            <span>{getTimeAgo(post.published_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between border-t border-gray-700 pt-4">
                        <div className="flex items-center space-x-3 space-x-reverse">
                          <button
                            onClick={() => toggleLike(post.id)}
                            className={`p-2 rounded-full transition-colors ${
                              likedPosts.has(post.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                            }`}
                          >
                            <Heart fill={likedPosts.has(post.id) ? 'currentColor' : 'none'} size={18} />
                          </button>
                          <button className="text-gray-400 hover:text-blue-400 p-2 rounded-full transition-colors">
                            <MessageCircle size={18} />
                          </button>
                        </div>
                        
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-yellow-400 hover:text-yellow-300 font-medium flex items-center space-x-1 space-x-reverse"
                        >
                          <span>مطالعه مقاله</span>
                          <ArrowLeft size={16} />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">هنوز مقاله‌ای منتشر نشده است.</p>
              </div>
            )}
          </div>
        </section> */}
        
        {/* About Us Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              
              {/* Content Side */}
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 space-x-reverse bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4">
                  <Star className="w-4 h-4" />
                  <span>درباره Spadana Techno Polymer</span>
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  {aboutData?.title || 'پیشرو در صنعت با ۲۰ سال تجربه'}
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  {aboutData?.subtitle || ' گوره صنعتی تکنو پلیمر اسپادانا  با تکیه بر دانش فنی و تجربه ارزشمند، همواره در ارائه محصولات با کیفیت و خدمات ممتاز پیشرو بوده است.'}
                </p>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3 space-x-reverse text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>تولید محصولات با استانداردهای بین‌المللی</span>
                  </div>
                  <div className="flex items-center space-x-3 space-x-reverse text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>پشتیبانی و خدمات پس از فروش ۲۴ ساعته</span>
                  </div>
                  <div className="flex items-center space-x-3 space-x-reverse text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>تیم متخصص و با تجربه در زمینه صنعت</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    to="/about"
                    className="inline-flex items-center justify-center space-x-2 space-x-reverse bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-all group"
                  >
                    <span>اطلاعات بیشتر</span>
                    <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
                  >
                    <span>تماس با ما</span>
                  </Link>
                </div>
              </div>

              {/* Image Side */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src={aboutData?.main_image ? getFullImageUrl(aboutData.main_image) : getFullImageUrl('/static/images/about-main.jpg')}
                    alt={aboutData?.title || 'درباره Spadana Techno Polymer'}
                    className="w-full h-96 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40 rounded-2xl"></div>
                </div>
                
                {/* Experience Badge */}
                <div className="absolute -top-4 -right-4 bg-yellow-400 text-gray-900 px-6 py-3 rounded-2xl shadow-lg">
                  <div className="text-2xl font-bold">۱۰+ سال</div>
                  <div className="text-sm font-medium">تجربه درخشان</div>
                </div>

                {/* Floating Stats */}
                <div className="absolute -bottom-4 -left-4 bg-gray-900 border border-gray-800 rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-yellow-400 text-gray-900 p-2 rounded-lg">
                      <Award className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-white font-bold"></div>
                      <div className="text-gray-400 text-sm">گواهی کیفیت</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Values Section - بخش جدید */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-white mb-4">
                ارزش‌های ما
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                آنچه Spadana Techno Polymer را از دیگران متمایز می‌کند
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(aboutData?.values && aboutData.values.length > 0 ? aboutData.values : defaultValues).map((value, index) => (
                <div 
                  key={value.id} 
                  className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-yellow-400 transition-all group hover:transform hover:-translate-y-2"
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-3 bg-yellow-400 text-gray-900 rounded-xl group-hover:scale-110 transition-transform">
                      {getIconComponent(value.icon)}
                    </div>
                    
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors">
                      {value.title}
                    </h3>
                    
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mission & Vision Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16">
              {/* Mission */}
              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-yellow-400 transition-all">
                <div className="flex items-center space-x-3 space-x-reverse mb-6">
                  <Target className="w-8 h-8 text-yellow-400" />
                  <h3 className="text-2xl font-bold text-white">
                    {aboutData?.mission_title || 'ماموریت ما'}
                  </h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  {aboutData?.mission_content ? 
                    aboutData.mission_content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' : 
                    'تولید محصولات صنعتی با کیفیت جهانی و ارائه خدمات تخصصی به مشتریان در سراسر کشور. ما متعهد به نوآوری و توسعه مستمر در صنعت هستیم.'
                  }
                </p>
              </div>

              {/* Vision */}
              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-yellow-400 transition-all">
                <div className="flex items-center space-x-3 space-x-reverse mb-6">
                  <EyeIcon className="w-8 h-8 text-yellow-400" />
                  <h3 className="text-2xl font-bold text-white">
                    {aboutData?.vision_title || 'چشم انداز ما'}
                  </h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  {aboutData?.vision_content ? 
                    aboutData.vision_content.replace(/<[^>]*>/g, '').substring(0, 150) + '...' : 
                    'تبدیل شدن به پیشروترین شرکت صنعتی در منطقه با تمرکز بر تکنولوژی‌های نوین و توسعه پایدار. ما به آینده‌ای روشن در صنعت می‌نگریم.'
                  }
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 space-x-reverse border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-lg font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all"
              >
                <span>مشاهده اطلاعات کامل درباره ما</span>
                <ArrowLeft size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-yellow-400 to-yellow-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              آماده همکاری با ما هستید؟
            </h2>
            <p className="text-gray-800 text-xl mb-8 max-w-2xl mx-auto">
              برای دریافت مشاوره رایگان و اطلاعات بیشتر درباره محصولات و خدمات ما، با ما در ارتباط باشید.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-gray-900 text-white px-8 py-4 rounded-full font-bold hover:bg-gray-800 transition-all transform hover:scale-105"
              >
                📞 تماس با ما
              </Link>
              <Link
                to="/products"
                className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-gray-900 hover:text-white transition-all transform hover:scale-105"
              >
                🛒 مشاهده محصولات
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

// کامپوننت کارت پست بلاگ
const BlogPostCard = ({ post, liked, onLike, timeAgo }) => {
  return (
    <article className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group">
      {/* Post Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFullImageUrl(post.featured_image)}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
          {post.category_name}
        </div>
        
        {/* Like Button */}
        <button
          onClick={onLike}
          className={`absolute top-4 right-4 p-2 rounded-full bg-black bg-opacity-50 transition-colors ${
            liked ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
          }`}
        >
          <Heart fill={liked ? 'currentColor' : 'none'} size={20} />
        </button>
      </div>

      {/* Post Content */}
      <div className="p-5">
        <h3 className="font-bold text-lg text-white mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
          {post.title}
        </h3>
        
        <p className="text-gray-300 text-sm mb-4 line-clamp-3">
          {post.excerpt}
        </p>
        
        {/* Post Meta */}
        <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
          <div className="flex items-center space-x-2 space-x-reverse">
            <User size={14} />
            <span>{post.author_name}</span>
          </div>
          <div className="flex items-center space-x-4 space-x-reverse">
            <div className="flex items-center space-x-1 space-x-reverse">
              <Eye size={14} />
              <span>{post.view_count || 0}</span>
            </div>
            <div className="flex items-center space-x-1 space-x-reverse">
              <Calendar size={14} />
              <span>{timeAgo}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between border-t border-gray-700 pt-4">
          <div className="flex items-center space-x-3 space-x-reverse">
            <button className="text-gray-400 hover:text-blue-400 p-1 rounded transition-colors">
              <MessageCircle size={16} />
            </button>
            <span className="text-gray-500 text-xs">
              {post.comment_count || 0} نظر
            </span>
          </div>
          
          <Link
            to={`/blog/${post.slug}`}
            className="text-yellow-400 hover:text-yellow-300 font-medium text-sm flex items-center space-x-1 space-x-reverse"
          >
            <span>مطالعه مقاله</span>
            <ChevronLeft size={16} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default Home;